
import { useState, useEffect, useRef } from "react";
import { useToast } from "@/hooks/use-toast";

export const usePositionTracking = (onPositionUpdate: (pos: GeolocationPosition) => void) => {
  const [tracking, setTracking] = useState(false);
  const workerRef = useRef<Worker | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    // Opret worker
    workerRef.current = new Worker(
      new URL('../workers/gps.worker.ts', import.meta.url),
      { type: 'module' }
    );

    // Håndter worker beskeder
    workerRef.current.onmessage = (e) => {
      const { type, data, message } = e.data;

      switch (type) {
        case 'position':
          // Konverter worker data til GeolocationPosition format
          const position = {
            coords: {
              latitude: data.latitude,
              longitude: data.longitude,
              accuracy: data.accuracy,
              altitude: null,
              altitudeAccuracy: null,
              heading: null,
              speed: null
            },
            timestamp: data.timestamp
          } as GeolocationPosition;
          
          onPositionUpdate(position);
          break;

        case 'error':
          toast({
            title: "GPS fejl",
            description: message,
            variant: "destructive",
          });
          break;

        case 'status':
          toast({
            title: "GPS Status",
            description: message,
          });
          break;
      }
    };

    // Cleanup worker ved unmount
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
    };
  }, [onPositionUpdate, toast]);

  const startTracking = () => {
    if (!workerRef.current) {
      toast({
        title: "Fejl",
        description: "GPS tracking kunne ikke initialiseres",
        variant: "destructive",
      });
      return false;
    }

    workerRef.current.postMessage({ action: 'start' });
    setTracking(true);
    return true;
  };

  const stopTracking = () => {
    if (workerRef.current) {
      workerRef.current.postMessage({ action: 'stop' });
      setTracking(false);
    }
  };

  return {
    tracking,
    startTracking,
    stopTracking
  };
};
